(function () {
  if (window.innerWidth < 1024) return;

  var observer = null;

  function applyFix() {
    var hm = document.querySelector('header-menu');
    if (!hm) return false;

    hm.classList.remove('hidden');
    hm.style.display = 'flex';
    hm.style.justifyContent = 'center';

    // Set up observer if not already watching
    if (!observer) {
      observer = new MutationObserver(function () {
        if (hm.classList.contains('hidden') || hm.style.display === 'none') {
          hm.classList.remove('hidden');
          hm.style.display = 'flex';
          hm.style.justifyContent = 'center';
        }
      });
      observer.observe(hm, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    return true;
  }

  // Keep checking every 500ms for 10 seconds after load
  var attempts = 0;
  var maxAttempts = 20; // 20 x 500ms = 10 seconds

  var interval = setInterval(function () {
    attempts++;
    applyFix();
    if (attempts >= maxAttempts) {
      clearInterval(interval);
      console.log('[nav-fix] done checking after 10s');
    }
  }, 500);

})();

(function() {
  // Define the top-level nav items we want to keep visible
  const protectedLinks = [
    '/collections/originals',
    '/collections/all-large-prints',
    '/collections/palette-knives',
    '/pages/commissions',
    '/pages/learning',
    '/pages/the-artist'
  ];

  // Find all top-level menu items
  const menuListItems = document.querySelectorAll('.menu-list__list-item');
  
  // Create observer to watch for slot attribute changes
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'slot') {
        const target = mutation.target;
        
        // Check if this is one of our protected menu items
        const link = target.querySelector('a[href]');
        if (link && protectedLinks.includes(link.getAttribute('href'))) {
          // If it got slot="overflow", remove it immediately
          if (target.getAttribute('slot') === 'overflow') {
            target.removeAttribute('slot');
            console.log('Protected item from overflow:', link.textContent.trim());
          }
        }
      }
    });
  });

  // Start observing all top-level menu items
  menuListItems.forEach(function(item) {
    observer.observe(item, {
      attributes: true,
      attributeFilter: ['slot']
    });
  });

  // Also run a periodic check as backup
  const intervalId = setInterval(function() {
    menuListItems.forEach(function(item) {
      const link = item.querySelector('a[href]');
      if (link && protectedLinks.includes(link.getAttribute('href'))) {
        if (item.getAttribute('slot') === 'overflow') {
          item.removeAttribute('slot');
          console.log('Backup protection removed overflow from:', link.textContent.trim());
        }
      }
    });
  }, 500); // Check every 500ms

  // Stop the periodic check after 10 seconds
  setTimeout(function() {
    clearInterval(intervalId);
    observer.disconnect();
    console.log('Overflow protection stopped after 10 seconds');
  }, 10000);

})();




import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}





